import { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Card,
  CardBody,
  CardHeader,
  Collapse,
  Flex,
  HStack,
  Icon,
  Spinner,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure
} from "@chakra-ui/react";
import {
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InfoOutlineIcon
} from "@chakra-ui/icons";
import { getContractService } from "../../../Services/api";
import { formatDate } from "../utils/formatDate";
import { SubPackageCard } from "./PackageCards";

export const HistoryItemWithServices = ({ historyItem, texts }) => {
  const { isOpen, onToggle } = useDisclosure();
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const cardBg = useColorModeValue("white", "gray.800");
  const headerBg = useColorModeValue("blue.50", "blue.900");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const titleColor = useColorModeValue("gray.800", "white");
  const mutedBg = useColorModeValue("gray.50", "gray.700");

  useEffect(() => {
    if (!isOpen || services.length > 0) return;

    let isActive = true;
    setIsLoading(true);
    setError(null);

    getContractService(historyItem.HistoryId)
      .then((response) => {
        if (!isActive) return;
        setServices(response.data?.data || []);
      })
      .catch((err) => {
        if (!isActive) return;
        setError(err.message || texts?.fetchError);
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [historyItem.HistoryId, isOpen, services.length, texts?.fetchError]);
  return (
    <Card
      width="100%"
      border="1px"
      borderColor={borderColor}
      shadow="md"
      bg={cardBg}
      _hover={{ shadow: "lg" }}
      transition="all 0.3s"
      borderRadius="xl"
      overflow="hidden"
    >
      <CardHeader
        bg={headerBg}
        borderBottom="1px"
        borderColor={borderColor}
        py={3}
      >
        <Flex
          justify="space-between"
          align="center"
          cursor="pointer"
          onClick={onToggle}
          _hover={{ opacity: 0.9 }}
        >
          <HStack spacing={4}>
            <Flex w="40px" h="40px" bg="blue.100" borderRadius="lg" align="center" justify="center">
              <Icon as={CalendarIcon} color="blue.600" boxSize={5} />
            </Flex>
            <VStack align="start" spacing={1}>
              <Text fontWeight="bold" fontSize="md" color={titleColor}>
                {formatDate(historyItem.HistoryDate)}
              </Text>
              {historyItem.Description && (
                <Text fontSize="sm" color="gray.600" noOfLines={1}>
                  {historyItem.Description}
                </Text>
              )}
            </VStack>
          </HStack>
          <Flex align="center" gap={2}>
            <Badge colorScheme="blue" variant="subtle">
              {texts?.subPackages}
            </Badge>
            <Icon
              as={isOpen ? ChevronUpIcon : ChevronDownIcon}
              w={5}
              h={5}
              color="blue.500"
              transition="transform 0.3s"
              transform={isOpen ? "rotate(180deg)" : "rotate(0)"}
            />
          </Flex>
        </Flex>
      </CardHeader>

      <Collapse in={isOpen} animateOpacity>
        <CardBody pt={4}>
          {isLoading ? (
            <VStack spacing={4} py={4}>
              <Spinner size="md" color="blue.500" />
              <Text color="gray.600">{texts?.loadingServices}</Text>
            </VStack>
          ) : error ? (
            <Alert status="error" borderRadius="md" size="sm">
              <AlertIcon />
              {error}
            </Alert>
          ) : services.length > 0 ? (
            <Box>
              <Flex justify="space-between" align="center" mb={4}>
                <Text fontWeight="semibold" color="gray.600">
                  {texts?.subPackages} ({services.length})
                </Text>
              </Flex>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {services.map((service, index) => (
                  <SubPackageCard key={service.Id || index} subPackage={service} texts={texts} />
                ))}
              </SimpleGrid>
            </Box>
          ) : (
            <Box
              textAlign="center"
              py={8}
              bg={mutedBg}
              borderRadius="lg"
            >
              <Icon as={InfoOutlineIcon} color="gray.400" boxSize={8} mb={3} />
              <Text color="gray.500" fontStyle="italic">
                {texts?.noServicesData}
              </Text>
            </Box>
          )}
        </CardBody>
      </Collapse>
    </Card>
  );
};
